// The per-symbol gap ticker: one row per symbol of /api/data/latest (pool price, feed price, gap in
// bps, the pool's actual fee, the Bell hook fee for that gap, feed age), sorted by absolute gap. With
// the API unreachable the rows come from the recorded snapshot and the columns the snapshot lacks
// read n/a. Feed age is a duration, never a statement about the market: the feeds carry a 24 hour
// heartbeat and a 0.5 percent deviation threshold, so a feed can be silent for hours inside a live
// session.
import type { LatestPayload, LatestSymbol } from '../api/types';
import { dur, fmtUsd, fmtUtc, parseTs } from './format';
import type { Snapshot } from './snapshot';

export interface TickerRow {
  key: string;
  sym: string;
  pool: string;
  feed: string;
  /** Signed gap in bps with one decimal, or n/a. */
  gap: string;
  gapClass: '' | 'pos' | 'neg';
  /** The pool's own fee tier in bps, "unknown" when the worker has none. */
  poolFee: string;
  /** The Bell hook fee for the measured gap, "unknown" when the worker could not derive it. */
  hookFee: string;
  feedAge: string;
}

export interface TickerModel {
  /** The line above the table; starts with "Recorded" when the rows come from the snapshot. */
  intro: string;
  /** Header of the feed age column, which names the reference time for recorded rows. */
  ageHead: string;
  rows: TickerRow[];
}

export const TICKER_HEADS = ['Symbol', 'Pool price', 'Feed price', 'Gap', 'Pool fee', 'Bell hook fee'] as const;
export const AGE_HEAD_LIVE = 'Feed age';
export const AGE_HEAD_RECORDED = 'Feed age at the reading';
export const FEED_NOTE = 'Feed age is the time since the feed last updated; the feeds carry a 24 hour heartbeat and a 0.5 percent deviation threshold, so a silent feed says nothing about the market.';

const isNum = (v: unknown): v is number => typeof v === 'number' && Number.isFinite(v);

/** Signed bps with one decimal: "+15.3", "-13.1", "0.0" stays "+0.0". */
export const bpsText = (v: number | null | undefined): string => (isNum(v) ? (v >= 0 ? '+' : '') + v.toFixed(1) : 'n/a');

/** A fee in bps: whole tiers as they are ("30", "100"), derived fees with one decimal, null as unknown. */
export const feeBpsText = (v: number | null | undefined): string =>
  !isNum(v) ? 'unknown' : Number.isInteger(v) ? String(v) : v.toFixed(1);

/** "5 min", "16 h 12 min", "2 d 15 h 3 min" for a feed age in seconds; null is n/a. */
export const feedAgeText = (s: number | null | undefined): string => (isNum(s) ? dur(s * 1000) : 'n/a');

const price = (v: number | null | undefined): string => (isNum(v) ? fmtUsd(v, 2) : 'n/a');
const gapClass = (v: number | null | undefined): TickerRow['gapClass'] => (isNum(v) ? (v >= 0 ? 'pos' : 'neg') : '');

interface Sortable { row: TickerRow; gapNum: number | null }

/** Absolute gap descending; rows without a gap last, then by symbol so the order is stable. */
function sortRows(list: Sortable[]): TickerRow[] {
  return list.sort((a, b) => {
    if (a.gapNum === null && b.gapNum === null) return a.row.sym.localeCompare(b.row.sym);
    if (a.gapNum === null) return 1;
    if (b.gapNum === null) return -1;
    return Math.abs(b.gapNum) - Math.abs(a.gapNum) || a.row.sym.localeCompare(b.row.sym);
  }).map((x) => x.row);
}

function rowFromApi(sym: string, s: LatestSymbol): Sortable {
  const gap = isNum(s.gap_bps) ? s.gap_bps : null;
  const row: TickerRow = {
    key: sym,
    sym,
    pool: price(s.pool_price ?? s.pool),
    feed: price(s.feed_price ?? s.oracle),
    gap: bpsText(gap),
    gapClass: gapClass(gap),
    poolFee: feeBpsText(s.actual_fee_bps),
    hookFee: feeBpsText(s.bellguard_fee_bps),
    feedAge: feedAgeText(s.feed_age_s ?? s.oracle_age_s),
  };
  return { row, gapNum: gap };
}

export function tickerFromApi(p: LatestPayload): TickerModel {
  const rows = sortRows(Object.entries(p.symbols).map(([sym, s]) => rowFromApi(sym, s)));
  const at = parseTs(p.as_of);
  return {
    intro: `${rows.length} symbols from the worker, indexed ${at ? fmtUtc(at) : 'time n/a'}, sorted by absolute gap; pool price is the last indexed swap of the selected pool. ${FEED_NOTE}`,
    ageHead: AGE_HEAD_LIVE,
    rows,
  };
}

/** Rows from the recorded snapshot: gap from its last readings, feed age at the time of that reading;
 *  the fee columns need the worker and read n/a. */
export function tickerFromSnapshot(snap: Snapshot): TickerModel {
  const rows = sortRows(Object.entries(snap.series).map(([sym, s]) => {
    const gap = isNum(s.last_dex) && isNum(s.last_oracle) && s.last_oracle !== 0 ? (s.last_dex / s.last_oracle - 1) * 10000 : null;
    const readAt = parseTs(s.last_ts), feedAt = parseTs(s.oracle_updated_at);
    const ageS = readAt && feedAt ? Math.max(0, (readAt.getTime() - feedAt.getTime()) / 1000) : null;
    const row: TickerRow = {
      key: sym,
      sym,
      pool: price(s.last_dex),
      feed: price(s.last_oracle),
      gap: bpsText(gap),
      gapClass: gapClass(gap),
      poolFee: 'n/a',
      hookFee: 'n/a',
      feedAge: feedAgeText(ageS),
    };
    return { row, gapNum: gap };
  }));
  const to = parseTs(snap.meta.recorded_to);
  return {
    intro: `Recorded snapshot, last readings ${to ? fmtUtc(to) : snap.meta.recorded_to}, ${rows.length} symbols sorted by absolute gap; the pool fee and the Bell hook fee need the live API. ${FEED_NOTE}`,
    ageHead: AGE_HEAD_RECORDED,
    rows,
  };
}
